/**
 * AdminListHeader - Standardized header for admin list views
 * 
 * Features:
 * - Title and subtitle
 * - Create button (role-aware)
 * - Refresh button
 * - Density toggle
 * - Additional actions slot
 */
import React from 'react';
import { Button } from '../ui/button';
import { DensityToggle } from './AdminDataTable';
import { Plus, RefreshCw } from 'lucide-react';
import { cn } from '../../lib/utils';

const AdminListHeader = ({
  // Title
  title,
  subtitle,
  totalCount,
  
  // Create action
  createLabel,
  onCreate,
  canCreate = true,
  CreateIcon = Plus,
  
  // Refresh
  onRefresh,
  refreshing = false,
  
  // Density
  density,
  onDensityChange,
  showDensityToggle = true,
  
  // Additional actions
  actions,
  
  // Styling
  className,
  'data-testid': testId = 'admin-list-header'
}) => {
  return (
    <div className={cn('flex items-center justify-between gap-4', className)} data-testid={testId}>
      {/* Left side - Title */}
      <div>
        <h1 className="text-2xl font-heading font-bold text-ivory-800">
          {title}
        </h1>
        {(subtitle || totalCount !== undefined) && (
          <p className="text-ivory-500 font-body text-sm mt-1">
            {subtitle || (totalCount !== undefined && `${totalCount.toLocaleString()} total`)}
          </p>
        )}
      </div>
      
      {/* Right side - Actions */}
      <div className="flex items-center gap-3">
        {/* Density toggle */}
        {showDensityToggle && density && onDensityChange && (
          <DensityToggle
            density={density}
            onDensityChange={onDensityChange}
          />
        )}
        
        {/* Additional actions */}
        {actions}
        
        {/* Refresh button */}
        {onRefresh && (
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={refreshing}
            data-testid="refresh-btn"
          >
            <RefreshCw className={cn('w-4 h-4 mr-2', refreshing && 'animate-spin')} />
            Refresh
          </Button>
        )}
        
        {/* Create button */}
        {onCreate && canCreate && (
          <Button
            onClick={onCreate}
            data-testid="create-btn"
          >
            <CreateIcon className="w-4 h-4 mr-2" />
            {createLabel || 'Create'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default AdminListHeader;
